import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { Pagination } from '@/components/ui/Pagination'
import { EmptyState } from '@/components/ui/EmptyState'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { useProspectsList, useProspectTags } from '@/hooks/useProspection'
import { useDebounce } from '@/hooks/useDebounce'
import {
  STATUT_LABELS,
  STATUT_COLORS,
  EMAIL_STATUS_LABELS,
  EMAIL_STATUS_COLORS,
} from '@/types/prospection'
import type { ProspectStatut } from '@/types/prospection'
import {
  Search,
  Target,
  Building2,
  MapPin,
  Globe,
  XCircle,
  Mail,
  Phone,
  PhoneCall,
  TrendingUp,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import CallPanel from '@/components/admin/prospection/CallPanel'
import {
  CampaignsList,
  CampaignWizard,
  CampaignDetail,
} from '@/components/admin/prospection/campaigns'

const ITEMS_PER_PAGE = 20

export default function ProspectionPage() {
  const [activeTab, setActiveTab] = useState('prospects')
  const [search, setSearch] = useState('')
  const [statutFilter, setStatutFilter] = useState<ProspectStatut | ''>('')
  const [siteFilter, setSiteFilter] = useState<string>('')
  const [tagFilter, setTagFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState('score_priorite')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [wizardOpen, setWizardOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)

  const debouncedSearch = useDebounce(search, 300)

  const { data: tagsData } = useProspectTags()

  const { data, isLoading } = useProspectsList({
    search: debouncedSearch,
    statut: statutFilter,
    hasWebsite: siteFilter === 'oui' ? true : siteFilter === 'non' ? false : null,
    tag: tagFilter,
    page,
    limit: ITEMS_PER_PAGE,
    sortBy,
    sortOrder,
  })

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortOrder('desc')
    }
    setPage(1)
  }

  function SortIcon({ column }: { column: string }) {
    if (sortBy !== column) return <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />
    return sortOrder === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5 text-mint-600" />
      : <ChevronDown className="w-3.5 h-3.5 text-mint-600" />
  }

  const totalPages = Math.ceil((data?.total || 0) / ITEMS_PER_PAGE)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Prospection
          </h1>
          <p className="mt-1 text-brown-600">
            {data?.total || 0} prospect{(data?.total || 0) > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="prospects">
            <span className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Prospects
            </span>
          </TabsTrigger>
          <TabsTrigger value="calls">
            <span className="flex items-center gap-2">
              <PhoneCall className="w-4 h-4" />
              Panel d'appel
            </span>
          </TabsTrigger>
          <TabsTrigger value="campaigns">
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Panel Email
            </span>
          </TabsTrigger>
        </TabsList>

        {/* Prospects list tab */}
        <TabsContent value="prospects">
          <div className="space-y-6">
            {/* Stats rapides */}
            {data && data.total > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card padding="sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Target className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-brown-900">{data.total}</p>
                      <p className="text-xs text-brown-500">Total prospects</p>
                    </div>
                  </div>
                </Card>
                <Card padding="sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-brown-900">
                        {data.prospects.filter((p) => !p.a_un_site).length}
                      </p>
                      <p className="text-xs text-brown-500">Sans site web</p>
                    </div>
                  </div>
                </Card>
                <Card padding="sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <Globe className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-brown-900">
                        {data.prospects.filter((p) => p.a_un_site).length}
                      </p>
                      <p className="text-xs text-brown-500">Avec site web</p>
                    </div>
                  </div>
                </Card>
                <Card padding="sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <Mail className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-brown-900">
                        {data.prospects.filter((p) => p.email).length}
                      </p>
                      <p className="text-xs text-brown-500">Avec email</p>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Filters */}
            <Card padding="sm">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 min-w-[200px]">
                  <Input
                    type="search"
                    placeholder="Rechercher par nom, ville, secteur, email..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value)
                      setPage(1)
                    }}
                    leftIcon={<Search className="w-4 h-4" />}
                  />
                </div>
                <div className="w-full sm:w-48">
                  <Select
                    value={statutFilter}
                    onChange={(e) => {
                      setStatutFilter(e.target.value as ProspectStatut | '')
                      setPage(1)
                    }}
                  >
                    <option value="">Tous les statuts</option>
                    {Object.entries(STATUT_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="w-full sm:w-48">
                  <Select
                    value={siteFilter}
                    onChange={(e) => {
                      setSiteFilter(e.target.value)
                      setPage(1)
                    }}
                  >
                    <option value="">Site web</option>
                    <option value="non">Sans site</option>
                    <option value="oui">Avec site</option>
                  </Select>
                </div>
                <div className="w-full sm:w-48">
                  <Select
                    value={tagFilter}
                    onChange={(e) => {
                      setTagFilter(e.target.value)
                      setPage(1)
                    }}
                  >
                    <option value="">Tous les tags</option>
                    {(tagsData || []).map((tag) => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </Card>

            {/* Content */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : data?.prospects?.length === 0 ? (
              <Card padding="lg">
                <EmptyState
                  icon={<Target className="w-12 h-12" />}
                  title="Aucun prospect"
                  description={
                    search || statutFilter || siteFilter || tagFilter
                      ? 'Aucun prospect ne correspond a vos criteres'
                      : 'Aucun prospect en base de donnees'
                  }
                />
              </Card>
            ) : (
              <Card padding="none">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-beige-200 bg-beige-50/50">
                        {([
                          { label: 'Entreprise', col: 'raison_sociale' },
                          { label: 'Secteur', col: 'secteur' },
                          { label: 'Localisation', col: 'ville' },
                          { label: 'Contact', col: null },
                          { label: 'Site web', col: 'a_un_site' },
                          { label: 'Statut', col: 'statut' },
                          { label: 'Email', col: 'email_status' },
                          { label: 'Score', col: 'score_priorite' },
                        ] as { label: string; col: string | null }[]).map(({ label, col }) => (
                          <th
                            key={label}
                            className={`text-left px-6 py-3 text-xs font-semibold text-brown-500 uppercase tracking-wider whitespace-nowrap ${col ? 'cursor-pointer select-none hover:text-brown-700' : ''}`}
                            onClick={col ? () => handleSort(col) : undefined}
                          >
                            <span className="inline-flex items-center gap-1">
                              {label}
                              {col && <SortIcon column={col} />}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-beige-100">
                      {data?.prospects.map((p) => (
                        <tr
                          key={p.id}
                          className="hover:bg-beige-50/50 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <Link
                              to={`/prospection/${p.id}`}
                              className="font-medium text-brown-900 hover:text-mint-600 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-brown-400 flex-shrink-0" />
                                <span>{p.nom_commercial || p.raison_sociale || '-'}</span>
                              </div>
                            </Link>
                            {p.nom_commercial && p.raison_sociale && p.nom_commercial !== p.raison_sociale && (
                              <p className="text-xs text-brown-400 mt-0.5 ml-6">
                                {p.raison_sociale}
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-brown-600 max-w-[160px] truncate">
                            {p.secteur || '-'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1 text-sm text-brown-600">
                              <MapPin className="w-3.5 h-3.5 text-brown-400 flex-shrink-0" />
                              <span>
                                {[p.ville, p.departement].filter(Boolean).join(' (') +
                                  (p.departement ? ')' : '') || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-0.5">
                              {p.email && (
                                <div className="flex items-center gap-1 text-xs text-brown-500">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate max-w-[150px]">{p.email}</span>
                                </div>
                              )}
                              {p.telephone && (
                                <div className="flex items-center gap-1 text-xs text-brown-500">
                                  <Phone className="w-3 h-3" />
                                  {p.telephone}
                                </div>
                              )}
                              {!p.email && !p.telephone && (
                                <span className="text-xs text-brown-400">-</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {p.a_un_site ? (
                              <div className="flex items-center gap-1">
                                <Globe className="w-4 h-4 text-green-500" />
                                {p.site_web ? (
                                  <a
                                    href={p.site_web}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-mint-600 hover:underline truncate max-w-[120px] block"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {p.site_web.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                                  </a>
                                ) : (
                                  <span className="text-xs text-green-600">Oui</span>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <XCircle className="w-4 h-4 text-red-400" />
                                <span className="text-xs text-red-500">Non</span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <Badge variant={STATUT_COLORS[p.statut || 'lead']}>
                              {STATUT_LABELS[p.statut || 'lead']}
                            </Badge>
                          </td>
                          <td className="px-6 py-4">
                            <Badge variant={EMAIL_STATUS_COLORS[p.email_status || 'pending']} size="sm">
                              {EMAIL_STATUS_LABELS[p.email_status || 'pending']}
                            </Badge>
                          </td>
                          <td className="px-6 py-4">
                            {p.score_priorite != null ? (
                              <div className="flex items-center gap-1">
                                <TrendingUp className="w-3.5 h-3.5 text-mint-600" />
                                <span className="text-sm font-medium text-brown-900">
                                  {p.score_priorite}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm text-brown-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            )}
          </div>
        </TabsContent>

        {/* Call Panel tab */}
        <TabsContent value="calls">
          <CallPanel />
        </TabsContent>

        {/* Email Campaigns tab */}
        <TabsContent value="campaigns">
          <CampaignsList
            onNew={() => setWizardOpen(true)}
            onView={(id) => setDetailId(id)}
          />
          {wizardOpen && (
            <CampaignWizard
              onClose={() => setWizardOpen(false)}
              onCreated={() => setWizardOpen(false)}
            />
          )}
          {detailId && (
            <CampaignDetail
              campaignId={detailId}
              onClose={() => setDetailId(null)}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
